import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import useGet from '@/core/composables/api/useGet'
import { FeedbackTopicType } from '@/core/models/feedback-topic'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import type { SurveySelectionPreview } from './use-coachee-evaluation-profile'

export function useCoacheeProfileExportOptions() {
  const route = useRoute()
  const coacheeId = computed(() => (route.params.coacheeId ?? route.params.id) as string)
  const endpoint = computed(() => `coaching/coachee/${coacheeId.value}/profile/pdf-options`)

  const {
    data: exportOptions,
    status,
    load,
  } = useGet<CoacheeProfileExportOptions>(endpoint)

  const selectedTopicIds = ref<string[]>([])
  const selectedSurveyIdsByTopicId = ref<Record<string, string[]>>({})

  const initSelection = (options: CoacheeProfileExportOptions) => {
    selectedTopicIds.value = options.feedbackTopics
      .filter((topic) => topic.type !== FeedbackTopicType.Evaluation || topic.surveys.length > 0)
      .map((topic) => topic.id)

    selectedSurveyIdsByTopicId.value = options.feedbackTopics.reduce<Record<string, string[]>>(
      (result, topic) => {
        if (topic.type === FeedbackTopicType.Evaluation) {
          result[topic.id] = topic.surveys?.map((survey) => survey.id) ?? []
        }
        return result
      },
      {},
    )
  }

  const loadExportOptions = async () => {
    await load()
    if (status.value === ApiRequestStatus.Success && exportOptions.value) {
      initSelection(exportOptions.value)
    }
  }

  onMounted(loadExportOptions)
  watch(coacheeId, loadExportOptions)

  const isTopicSelected = (topicId: string) => {
    return selectedTopicIds.value.includes(topicId)
  }

  const toggleTopic = (topicId: string) => {
    if (isTopicSelected(topicId)) {
      selectedTopicIds.value = selectedTopicIds.value.filter((id) => id !== topicId)
      return
    }
    selectedTopicIds.value.push(topicId)
  }

  const isSurveySelected = (topicId: string, surveyId: string) => {
    return selectedSurveyIdsByTopicId.value[topicId]?.includes(surveyId) ?? false
  }

  const toggleSurvey = (topicId: string, surveyId: string) => {
    const selectedSurveyIds = selectedSurveyIdsByTopicId.value[topicId] ?? []
    if (selectedSurveyIds.includes(surveyId)) {
      selectedSurveyIdsByTopicId.value[topicId] = selectedSurveyIds.filter((id) => id !== surveyId)
      return
    }
    selectedSurveyIdsByTopicId.value[topicId] = [...selectedSurveyIds, surveyId]
  }

  const payload = computed<CoacheeProfilePdfPayload>(() => {
    const options = exportOptions.value
    if (!options) {
      return { topics: [] }
    }

    return {
      topics: options.feedbackTopics
        .filter((topic) => isTopicSelected(topic.id))
        .map((topic) => {
          if (topic.type !== FeedbackTopicType.Evaluation) {
            return {
              feedbackTopicId: topic.id,
            }
          }

          return {
            feedbackTopicId: topic.id,
            surveyIds: [...(selectedSurveyIdsByTopicId.value[topic.id] ?? [])],
          }
        }),
    }
  })

  return {
    exportOptions,
    status,
    toggleTopic,
    isSurveySelected,
    toggleSurvey,
    isTopicSelected,
    payload,
  }
}

export type CoacheeProfilePdfTopicOption =
  | {
      id: string
      title: string
      icon: string
      type: FeedbackTopicType.Journal
    }
  | {
      id: string
      title: string
      icon: string
      type: FeedbackTopicType.Evaluation
      readonly surveys: ReadonlyArray<SurveySelectionPreview>
    }

export interface CoacheeProfileExportOptions {
  feedbackTopics: ReadonlyArray<CoacheeProfilePdfTopicOption>
}

export interface CoacheeProfilePdfPayload {
  topics: Array<{
    feedbackTopicId: string
    surveyIds?: string[]
  }>
}
